import { marked } from 'marked';
import { removeTagsAndContent } from './utils';

export class OfficeUtils {
  /**
   * Retrieves the currently selected text in the document.
   */
  public static async getSelectedText(): Promise<string> {
    await Office.onReady();
    try {
      if (this.isWord()) {
        return await this.getSelectedTextFromWord();
      } else if (this.isOutlook()) {
        return await this.getSelectedTextFromOutlook();
      }
      return '';
    } catch (error) {
      console.error('Error retrieving selected text:', error);
      throw error;
    }
  }

  /**
   * Retrieves the plain text of the entire document.
   */
  public static async getDocumentPlainText(): Promise<string> {
    await Office.onReady();
    try {
      if (this.isWord()) {
        return await this.getDocumentPlainTextFromWord();
      } else if (this.isOutlook()) {
        return await this.getDocumentPlainTextFromOutlook();
      }
      return '';
    } catch (error) {
      console.error('Error retrieving document text:', error);
      throw error;
    }
  }

  /**
   * Starts listening for document selection changes.
   * @param handler - The async handler to call when the selection changes.
   */
  public static startDocumentSelectionListener(
    handler: () => Promise<void>
  ): void {
    Office.onReady().then(() => {
      if (this.isWord()) {
        Office.context.document?.addHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          handler,
          (result: Office.AsyncResult<void>) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              console.error(
                'Error registering selection changed handler:',
                result.error
              );
            }
          }
        );
      }
    });
  }

  /**
   * Stops listening for document selection changes.
   * @param handler - The handler that was previously registered.
   */
  public static stopDocumentSelectionListener(
    handler: () => Promise<void>
  ): void {
    Office.onReady().then(() => {
      if (this.isWord()) {
        Office.context.document?.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler },
          (result: Office.AsyncResult<void>) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              console.error(
                'Error removing selection changed handler:',
                result.error
              );
            }
          }
        );
      }
    });
  }

  public static async clearTextSelection(): Promise<void> {
    await Office.onReady();
    try {
      if (this.isWord()) {
        await Word.run(async (context: Word.RequestContext): Promise<void> => {
          const selection = context.document.getSelection();
          const collapsedRange = selection.getRange('Start');
          collapsedRange.select();
          await context.sync();
        });
      }
    } catch (error) {
      console.error('Error clearing text selection:', error);
    }
  }

  public static async copyTextToClipboard(text: string): Promise<void> {
    try {
      const cleanHtml = await this.generateCleanHtml(text);
      const fontName = 'Calibri';
      const fontColor = '#000000';
      const fontSize = 11;
      const styledHtml = this.wrapHtmlWithFontStyles(
        cleanHtml,
        fontName,
        fontSize,
        fontColor
      );

      const plainText = await this.preparePlainText(text);

      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({
              'text/html': new Blob([styledHtml], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' })
            })
          ]);
          return;
        } catch {}
      }
      await this.writeToClipboard(plainText);
    } catch (err) {
      console.error('Error copying text to clipboard: ', err);
      throw err;
    }
  }

  public static async insertTextToDocument(text: string): Promise<void> {
    if (!text) {
      return;
    }

    await Office.onReady();

    try {
      if (this.isWord()) {
        await this.insertTextToWord(text);
      } else if (this.isOutlook()) {
        await this.insertTextToOutlook(text);
      }
    } catch (error) {
      console.error('Error inserting text:', error);
      throw error;
    }
  }

  /**
   * Retrieves all paragraphs of the document as an array of strings.
   */
  public static async getDocumentParagraphs(): Promise<string[]> {
    await Office.onReady();
    try {
      let paragraphs: string[] = [];
      await Word.run(async (context: Word.RequestContext) => {
        const body = context.document.body;
        const paragraphsCollection = body.paragraphs;
        paragraphsCollection.load('items');
        await context.sync();

        paragraphs = paragraphsCollection.items.map(
          (paragraph) => paragraph.text
        );
      });
      return paragraphs;
    } catch (error) {
      console.error('Error retrieving document paragraphs:', error);
      throw error;
    }
  }

  /**
   * Scrolls to and selects a single paragraph or a range of paragraphs.
   * @param startIndex - Zero-based index of the start paragraph.
   * @param endIndex - Zero-based index of the end paragraph (optional). If provided, selects the entire range.
   */
  public static async scrollToAndSelectParagraph(
    startIndex: number,
    endIndex?: number
  ): Promise<void> {
    await Office.onReady();

    try {
      await Word.run(async (context: Word.RequestContext) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        if (startIndex < 0 || startIndex >= paragraphs.items.length) {
          throw new Error('Start index is out of range.');
        }

        if (endIndex === undefined) {
          const para = paragraphs.items[startIndex];
          para.select();
          await context.sync();
          return;
        }

        if (endIndex < 0 || endIndex >= paragraphs.items.length) {
          throw new Error('End index is out of range.');
        }
        if (endIndex < startIndex) {
          throw new Error(
            'End index must be greater than or equal to start index.'
          );
        }

        const startPara = paragraphs.items[startIndex];
        const endPara = paragraphs.items[endIndex];

        const startRange = startPara.getRange(Word.RangeLocation.start);
        const endRange = endPara.getRange(Word.RangeLocation.end);

        const selectionRange = startRange.expandTo(endRange);
        selectionRange.select();

        await context.sync();
      });
    } catch (error) {
      console.error('Error scrolling to and selecting paragraph(s):', error);
      throw error;
    }
  }

  /**
   * Returns the name of the current document.
   * If the document is not saved or the URL is not available, returns 'Untitled Document'.
   */
  public static getDocumentName(): string {
    const docUrl = Office.context.document?.url;

    if (!docUrl) {
      return 'Untitled Document';
    }

    const segments = docUrl.split('/');
    const fileName = segments.pop();

    return fileName || 'Untitled Document';
  }

  /**
   * Scrolls to and selects the paragraphs at the specified indices sequentially,
   * with a 800ms delay between each selection.
   * @param paragraphIndices - Array of zero-based indices of the paragraphs to scroll to and select.
   */
  public static async scrollToAndSelectParagraphsSequentially(
    paragraphIndices: number[]
  ): Promise<void> {
    const delay = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    for (const index of paragraphIndices) {
      await this.scrollToAndSelectParagraph(index);
      await delay(800);
    }
  }

  /**
   * Gets the number of words in the current document.
   */
  public static async getDocumentWordCount(): Promise<number> {
    await Office.onReady();
    try {
      if (this.isWord()) {
        return await this.getDocumentWordCountFromWord();
      } else if (this.isOutlook()) {
        return await this.getDocumentWordCountFromOutlook();
      }
      return 0;
    } catch (error) {
      console.error('Error retrieving document word count:', error);
      throw error;
    }
  }

  public static isWord(): boolean {
    return !!Office.context.document;
  }

  public static isOutlook(): boolean {
    return !!Office.context.mailbox;
  }

  // PRIVATE METHODS

  /**
   * Converts markdown text to clean plain text, removing tags and formatting for clipboard or fallback use.
   */
  private static async preparePlainText(text: string): Promise<string> {
    const textWithoutTags = removeTagsAndContent(text, ['referenceDoc']);
    const htmlContent = await marked.parse(textWithoutTags);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    return (tempDiv.textContent || tempDiv.innerText || '')
      .replace(/\s+([.!?,:;])/g, '$1')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
  }

  /**
   * Writes plain text to the clipboard, using a fallback for older browsers.
   */
  private static async writeToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  /**
   * Gets selected text from Word
   */
  private static async getSelectedTextFromWord(): Promise<string> {
    let selectedText = '';
    await Word.run(async (context: Word.RequestContext): Promise<void> => {
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();
      selectedText = selection.text;
    });
    return selectedText;
  }

  /**
   * Gets selected text from Outlook
   */
  private static async getSelectedTextFromOutlook(): Promise<string> {
    return new Promise((resolve) => {
      const item = Office.context.mailbox.item;
      if (item) {
        item.getSelectedDataAsync(
          Office.CoercionType.Text,
          (result: Office.AsyncResult<string>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve(result.value || '');
            } else {
              console.error(
                'Error getting selected text in Outlook:',
                result.error
              );
              resolve('');
            }
          }
        );
      } else {
        console.error('Office.context.mailbox.item is undefined.');
        resolve('');
      }
    });
  }

  /**
   * Counts the words in the main body of the document
   */
  private static async getDocumentWordCountFromWord(): Promise<number> {
    return Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load('text');
      await context.sync();

      let total = 0;
      for (const p of paragraphs.items) {
        total += (p.text.match(/\S+/g) ?? []).length;
      }
      return total;
    });
  }

  /**
   * Gets word count from Outlook item
   */
  private static async getDocumentWordCountFromOutlook(): Promise<number> {
    let wordCount = 0;
    const text = await this.getDocumentPlainTextFromOutlook();
    // Split by whitespace and filter out empty strings
    wordCount = text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    return wordCount;
  }

  /**
   * Gets plain text of the entire document from Word
   */
  private static async getDocumentPlainTextFromWord(): Promise<string> {
    let documentText = '';
    await Word.run(async (context: Word.RequestContext): Promise<void> => {
      const body = context.document.body;
      body.load('text');
      await context.sync();
      documentText = body.text;
    });
    return documentText;
  }

  /**
   * Gets plain text of the entire document from Outlook
   */
  private static async getDocumentPlainTextFromOutlook(): Promise<string> {
    return new Promise((resolve) => {
      const item = Office.context.mailbox.item;
      if (item && item.body) {
        item.body.getAsync(
          Office.CoercionType.Text,
          (result: Office.AsyncResult<string>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve(result.value || '');
            } else {
              console.error(
                'Error getting document text in Outlook:',
                result.error
              );
              resolve('');
            }
          }
        );
      } else {
        console.error('Office.context.mailbox.item or item.body is undefined.');
        resolve('');
      }
    });
  }

  /**
   * Converts markdown text to cleaned HTML, removing specific tags and extra spaces.
   */
  private static async generateCleanHtml(text: string): Promise<string> {
    const htmlContent = await marked.parse(
      removeTagsAndContent(text, ['referenceDoc'])
    );
    return htmlContent.replace(/\s+([.!?,:;])/g, '$1').replace(/\s+/g, ' ');
  }

  /**
   * Wraps HTML content in a styled div with the given font, size, and color.
   */
  private static wrapHtmlWithFontStyles(
    html: string,
    fontName: string,
    fontSize: number,
    fontColor: string
  ): string {
    return `<div style="font-family: ${fontName}; font-size: ${fontSize}pt; color: ${fontColor};">${html}</div>`;
  }

  /**
   * Inserts text into a Word document
   */
  private static async insertTextToWord(text: string): Promise<void> {
    await Word.run(async function (context) {
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();

      const textSelection = selection.text;

      let currentRange = selection;
      currentRange = currentRange.getRange('Start');

      currentRange.load('font/name, font/size, font/color');
      currentRange.load(
        'font/bold, font/italic, font/underline, font/strikeThrough'
      );
      currentRange.load('font/highlightColor');
      await context.sync();

      let htmlContent = await marked.parse(
        removeTagsAndContent(text, ['referenceDoc'])
      );

      console.log('Font details:', {
        name: currentRange.font.name,
        size: currentRange.font.size,
        color: currentRange.font.color,
        bold: currentRange.font.bold,
        italic: currentRange.font.italic,
        underline: currentRange.font.underline,
        strikeThrough: currentRange.font.strikeThrough,
        highlightColor: currentRange.font.highlightColor
      });

      htmlContent = htmlContent
        .replace(/\s+([.!?,:;])/g, '$1')
        .replace(/\s+/g, ' ');

      const fontName = currentRange.font.name || 'Calibri';
      const fontColor = currentRange.font.color || '#000000';

      const fontSize =
        currentRange.font.size && currentRange.font.size > 1
          ? Math.round(currentRange.font.size * 100) / 100 // Round to 2 decimal places
          : 11;

      let styledHtml = `<div style="font-family: ${fontName}; font-size: ${fontSize}pt; color: ${fontColor};`;
      styledHtml += currentRange.font.bold ? ' font-weight: bold;' : '';
      styledHtml += currentRange.font.italic ? ' font-style: italic;' : '';
      styledHtml +=
        currentRange.font.underline !== 'None'
          ? ' text-decoration: underline;'
          : '';
      styledHtml += currentRange.font.strikeThrough
        ? ' text-decoration: line-through;'
        : '';
      styledHtml += currentRange.font.highlightColor
        ? ` background-color: ${currentRange.font.highlightColor};`
        : '';

      styledHtml += `">${htmlContent}</div>`;

      if (textSelection.length > 0) {
        selection.insertHtml(styledHtml, Word.InsertLocation.replace);
      } else {
        selection.insertHtml(styledHtml, Word.InsertLocation.after);
      }

      return context.sync();
    });
  }

  /**
   * Inserts text into an Outlook item
   */
  private static async insertTextToOutlook(text: string): Promise<void> {
    let textToInsert = await marked.parse(
      removeTagsAndContent(text, ['referenceDoc'])
    );

    textToInsert = textToInsert
      .replace(/\s+([.!?,:;])/g, '$1')
      .replace(/\s+/g, ' ');

    const plainText = textToInsert.replace(/<[^>]*>/g, '');

    return new Promise<void>((resolve, reject) => {
      const item = Office.context.mailbox.item;

      if (
        item &&
        typeof Office.context.mailbox.item !== 'undefined' &&
        Office.context.mailbox.item.itemType ===
          Office.MailboxEnums.ItemType.Message
      ) {
        item.body.setSelectedDataAsync(
          textToInsert,
          { coercionType: Office.CoercionType.Html },
          (result: Office.AsyncResult<void>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve();
            } else {
              item.body.setSelectedDataAsync(
                plainText,
                { coercionType: Office.CoercionType.Text },
                (textResult: Office.AsyncResult<void>) => {
                  if (
                    textResult.status === Office.AsyncResultStatus.Succeeded
                  ) {
                    resolve();
                  } else {
                    console.error(
                      'Error inserting text in Outlook:',
                      textResult.error
                    );
                    reject(textResult.error);
                  }
                }
              );
            }
          }
        );
      } else {
        console.warn(
          'Cannot insert text: Not in compose mode or item not available'
        );
        resolve();
      }
    });
  }
}
